import {useMutation} from "@apollo/client";
import {useMemo, useRef} from "react";

import {
  AnalyzerRunFailureInput,
  AnalyzerRunTurnInput,
  AnalyzerSource,
  AnalyzerTurnEnd,
  AnalyzerTurnType,
  GQL_RECORD_ANALYZER_RUN_FAILURE,
  GQL_RECORD_ANALYZER_RUN_START,
  GQL_RECORD_ANALYZER_RUN_TURN,
} from "@/lib/queries/analyzerRuns.ts";
import {GenericMutationResponse} from "@/lib/types/common.ts";
import {ANALYZER_SILENT_MUTATION} from "@/pages/analyzer/Analyzer.constants.ts";
import {AnalyzerRunTrackingState, AnalyzerTurnState, SSEEvent, SSEEventType} from "@/pages/analyzer/Analyzer.types.ts";
import {
  guardTracking,
  logTrackingFailure,
  logTrackingRejection,
  toUsageInput,
} from "@/pages/analyzer/Analyzer.utils.ts";

export const useAnalyzerRunTracking = () => {
  const [recordStart] = useMutation<GenericMutationResponse>(GQL_RECORD_ANALYZER_RUN_START, ANALYZER_SILENT_MUTATION);
  const [recordTurn] = useMutation<GenericMutationResponse>(GQL_RECORD_ANALYZER_RUN_TURN, ANALYZER_SILENT_MUTATION);
  const [recordFailure] = useMutation<GenericMutationResponse>(
    GQL_RECORD_ANALYZER_RUN_FAILURE,
    ANALYZER_SILENT_MUTATION
  );
  const runRef = useRef<AnalyzerRunTrackingState>({url: "", nextTurnIndex: 0});
  const turnRef = useRef<AnalyzerTurnState | null>(null);

  return useMemo(() => {
    const recordPreStartFailure = (errorCode?: string) => {
      const {url, source} = runRef.current;
      const input: AnalyzerRunFailureInput = {
        url,
        source,
        ...(errorCode && {error_code: errorCode}),
      };
      recordFailure({variables: {input}}).then(logTrackingRejection).catch(logTrackingFailure);
    };

    const beginTurn = (type: AnalyzerTurnType, {url, source}: {url?: string; source?: AnalyzerSource}) => {
      if (type === AnalyzerTurnType.INITIAL) {
        runRef.current = {
          url: url ?? runRef.current.url,
          source: source ?? runRef.current.source,
          nextTurnIndex: 0,
        };
      }
      turnRef.current = {index: runRef.current.nextTurnIndex, type, failed: false, reported: false};
    };

    const endTurn = (end: AnalyzerTurnEnd) => {
      const turn = turnRef.current;
      const run = runRef.current;
      if (!turn || turn.reported) {
        return;
      }
      turn.reported = true;
      let turnEnd = end;
      if (end === AnalyzerTurnEnd.DONE && turn.failed) {
        turnEnd = AnalyzerTurnEnd.ERROR;
      } else if (end === AnalyzerTurnEnd.DONE && !turn.doneEventReceived) {
        // a cut connection can surface as a clean EOF: without the server's done event it is a loss
        turnEnd = AnalyzerTurnEnd.CONNECTION_LOST;
      }
      if (!run.threadId) {
        if (turnEnd !== AnalyzerTurnEnd.ABORTED) {
          const fallbackCode = turnEnd === AnalyzerTurnEnd.DONE ? undefined : turnEnd.toLowerCase();
          recordPreStartFailure(turn.errorCode ?? fallbackCode);
        }
        return;
      }
      run.nextTurnIndex = turn.index + 1;
      const usage = turn.usage ?? turn.lastUsage;
      const input: AnalyzerRunTurnInput = {
        thread_id: run.threadId,
        turn_index: turn.index,
        turn_type: turn.type,
        turn_end: turnEnd,
        ...(turn.errorCode && {error_code: turn.errorCode}),
        ...(turn.revisionSuccess !== undefined && {revision_success: turn.revisionSuccess}),
        ...(usage && {usage: toUsageInput(usage)}),
        ...(turn.elapsed !== undefined && {elapsed_seconds: turn.elapsed}),
        ...(turn.proposalsCount !== undefined && {proposals_count: turn.proposalsCount}),
      };
      recordTurn({variables: {input}}).then(logTrackingRejection).catch(logTrackingFailure);
    };

    const onEvent = (event: SSEEvent) => {
      const turn = turnRef.current;
      const run = runRef.current;
      if (!turn) {
        return;
      }
      if (event.usage && event.type !== SSEEventType.DONE) {
        turn.lastUsage = event.usage;
      }
      switch (event.type) {
        case SSEEventType.STARTED:
          if (run.threadId !== event.thread_id) {
            run.threadId = event.thread_id;
            recordStart({
              variables: {input: {thread_id: event.thread_id, url: run.url, source: run.source}},
            })
              .then(logTrackingRejection)
              .catch(logTrackingFailure);
          }
          break;
        case SSEEventType.CHECK_SELECTION:
          turn.proposalsCount = event.checks.length;
          if (turn.type === AnalyzerTurnType.REVISE && event.success !== undefined) {
            turn.revisionSuccess = event.success;
          }
          break;
        case SSEEventType.ERROR:
          turn.failed = true;
          turn.errorCode = event.code ?? "error";
          break;
        case SSEEventType.DONE:
          turn.doneEventReceived = true;
          turn.usage = event.usage;
          turn.elapsed = event.elapsed;
          break;
      }
    };

    const onRequestFailed = (status: number) => {
      const turn = turnRef.current;
      if (!runRef.current.threadId) {
        if (turn) {
          turn.reported = true;
        }
        recordPreStartFailure(`http_${status}`);
        return;
      }
      if (turn) {
        turn.failed = true;
        turn.errorCode = `http_${status}`;
      }
      endTurn(AnalyzerTurnEnd.ERROR);
    };

    return {
      beginTurn: guardTracking(beginTurn),
      endTurn: guardTracking(endTurn),
      onEvent: guardTracking(onEvent),
      onRequestFailed: guardTracking(onRequestFailed),
    };
  }, [recordStart, recordTurn, recordFailure]);
};
